import { Injectable } from '@angular/core';
import {
  ConferenceRegistrationCampaignEmail,
  EmailTemplates,
  EmailToSendConfig,
  NotificationOptInGroup,
  NotifyEmailKeys,
} from 'ag-common-lib/lib';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import {
  BaseCampaignsEmail,
  BaseCampaignsEmailKeys,
  EmailAddressKeys,
  EmailTemplateConfiguration,
  EmailTemplateConfigurationKeys,
  EmailTemplatesDetailsMap,
  Registrant,
  RegistrantKeys,
  RegistrantModelKeys,
  RegistrantWizardStateKeys,
} from 'ag-common-lib/public-api';
import { ConferenceRegistrationEmailCampaignService } from 'ag-common-svc/lib/services/email-campaigns/conference-registration-email-campaign.service';
import { RegistrationSummaryEmailService } from 'ag-common-svc/lib/services/registration-summary-email/registration-summary-email.service';
import { confirm } from 'devextreme/ui/dialog';
import { Options } from 'nodemailer/lib/mailer';

export class SendRegistrantsEmailPayload {
  registrants?: Registrant[];
  optInGroup?: NotificationOptInGroup;
  isTestEmail?: boolean;
  isSystemEmail?: boolean;
  emailRecipientTestMode?: string[];
}

@Injectable()
export class AttendeesEmailsService {
  private readonly dynamicTemplatePrefix = 'dynamic:';

  constructor(
    private conferenceRegistrationEmailCampaignService: ConferenceRegistrationEmailCampaignService<any>,
    private registrationSummaryEmailService: RegistrationSummaryEmailService,
  ) {}

  sendEmails = async (emailTemplate: EmailTemplateConfiguration, payload: SendRegistrantsEmailPayload) => {
    const templateName = emailTemplate?.[EmailTemplateConfigurationKeys.templateName] as EmailTemplates | string;
    const templateConfiguration = this.isDynamicTemplateName(templateName)
      ? null
      : EmailTemplatesDetailsMap.get(templateName as EmailTemplates);
    const description = templateConfiguration?.title || emailTemplate?.[EmailTemplateConfigurationKeys.subject] || templateName;
    const registrants = payload?.registrants;
    const registrantsNumber = registrants.length;
    const isNotSubmitted = registrants.some(
      registrant => !registrant?.[RegistrantModelKeys.wizardState]?.[RegistrantWizardStateKeys.isSubmitted],
    );

    let confirmationMessage = '';
    if (isNotSubmitted) {
      confirmationMessage +=
        registrantsNumber > 1
          ? '<div>Not all registrants completed they registration.</div>'
          : '<div>Registration is not completed.</div>';
    }

    const inviteeText =
      registrantsNumber > 1
        ? `(${registrantsNumber}) Invitees`
        : registrants?.[0]?.[RegistrantModelKeys.data]?.[RegistrantKeys.inviteeEmail];

    confirmationMessage += `<div>Are you sure you want to Send "${description}" Email to ${inviteeText}?</div>`;

    const isConfirmed = payload?.isTestEmail || (await confirm(confirmationMessage, 'Confirm'));

    if (!isConfirmed) {
      return;
    }

    if (this.isDynamicTemplateName(templateName)) {
      await this.sendDynamicTemplateMassEmail(emailTemplate, payload, templateName);
      return;
    }

    switch (templateName) {
      case EmailTemplates.notifyEmailConferenceRegistrationShortSummary:
      case EmailTemplates.notifyEmailConferenceRegistrationSummary:
      case EmailTemplates.notifyEmailWinterSummitRegistrationSummary:
        await this.sendRegistrationSummaryMassEmail(emailTemplate, payload);
        return;
      case EmailTemplates.notifyEmailKnowBeforeYouGoToConference:
      case EmailTemplates.notifyEmailWinterSummitKBYG:
      case EmailTemplates.notifyEmailWinterSummitKBYGPdfAttache:
      case EmailTemplates.notifyEmailWinterSummitKBYG2026PdfAttache:
      case EmailTemplates.notifyEmailKnowBeforeYouGoToArizona:
      case EmailTemplates.notifyEmailKnowBeforeYouGoToBahiaMita:
        await this.sendBeforeYouGoMassEmail(emailTemplate, payload);
        return;

      case EmailTemplates.notifyEmailInviteesActivities:
      case EmailTemplates.notifyEmailInviteesActivitiesFirstDay:
      case EmailTemplates.notifyEmailInviteesActivitiesSecondDay:
        await this.sendInviteesActivitiesByDayMassEmail(emailTemplate, payload);
        return;
    }
  };

  private sendDynamicTemplateMassEmail = async (
    emailTemplate: EmailTemplateConfiguration,
    payload: SendRegistrantsEmailPayload,
    templateName: string,
  ): Promise<void> => {
    const templateDbId = this.getDynamicTemplateId(templateName);
    if (!templateDbId) {
      return;
    }

    const subject = emailTemplate?.[EmailTemplateConfigurationKeys.subject];
    const sender = emailTemplate?.[EmailTemplateConfigurationKeys.sender];
    const attachments = emailTemplate?.[EmailTemplateConfigurationKeys.attachments];
    const requiredFields = emailTemplate?.[EmailTemplateConfigurationKeys.payload]?.requiredFields;
    const promises: Promise<ConferenceRegistrationCampaignEmail | void>[] = [];

    payload?.registrants?.forEach(registrant => {
      const conferenceDbId = registrant?.[RegistrantModelKeys.conferenceDbId];
      const registrantDbId = registrant?.[BaseModelKeys.dbId];
      const to = payload?.isTestEmail ? payload?.emailRecipientTestMode : this.getRegistrantRecipients(registrant);

      if (!conferenceDbId || !registrantDbId || !subject || !to?.length) {
        return;
      }

      const campaignEmailPayload = Object.assign({}, new BaseCampaignsEmail(registrantDbId), {
        [BaseCampaignsEmailKeys.template]: templateName as unknown as EmailTemplates,
        templateDbId,
        subject,
        to,
        registrantDbId,
        conferenceDbId,
        sender,
        attachments,
        requiredFields,
      });

      promises.push(
        this.conferenceRegistrationEmailCampaignService.sendEmailToRegistrant(
          conferenceDbId,
          registrantDbId,
          campaignEmailPayload as any,
        ),
      );
    });

    await Promise.all(promises);
  };

  private sendInviteesActivitiesByDayMassEmail = async (
    emailTemplate: EmailTemplateConfiguration,
    payload: SendRegistrantsEmailPayload,
  ): Promise<void> => {
    const promises: Promise<any>[] = [];
    const templateName = emailTemplate?.[EmailTemplateConfigurationKeys.templateName] as EmailTemplates;
    debugger;
    payload?.registrants.forEach(registrant => {
      const conferenceDbId = registrant?.[RegistrantModelKeys.conferenceDbId];
      const registrantDbId = registrant?.[BaseModelKeys.dbId];

      const promise: Promise<ConferenceRegistrationCampaignEmail | void> = this.registrationSummaryEmailService
        .generateActivitiesEmail(emailTemplate, {
          conferenceDbId,
          registrantDbId,
          isTestEmail: payload?.isTestEmail,
          emailRecipientTestMode: payload?.emailRecipientTestMode,
        })
        .then((context): ConferenceRegistrationCampaignEmail => {
          const mailOptions = AttendeesEmailsService.getMailOptions(context);

          return Object.assign({}, new BaseCampaignsEmail(registrantDbId), {
            [BaseCampaignsEmailKeys.mailOptions]: mailOptions,
            [BaseCampaignsEmailKeys.context]: context,
            [BaseCampaignsEmailKeys.template]: templateName,
          });
        })
        .then(data =>
          this.conferenceRegistrationEmailCampaignService.sendEmailToRegistrant(
            conferenceDbId,
            registrantDbId,
            data as any,
          ),
        );

      promises.push(promise);
    });

    await Promise.all(promises);
  };

  private sendRegistrationSummaryMassEmail = async (
    emailTemplate: EmailTemplateConfiguration,
    payload: SendRegistrantsEmailPayload,
  ): Promise<void> => {
    const promises: Promise<EmailToSendConfig>[] = [];
    const templateName = emailTemplate?.[EmailTemplateConfigurationKeys.templateName] as EmailTemplates;

    payload?.registrants.forEach(registrant => {
      const conferenceDbId = registrant?.[RegistrantModelKeys.conferenceDbId];
      const registrantDbId = registrant?.[BaseModelKeys.dbId];

      const promise: Promise<any> = this.registrationSummaryEmailService
        .generateRegistrationSummaryEmail(emailTemplate, {
          conferenceDbId,
          registrantDbId,
          isTestEmail: payload?.isTestEmail,
          emailRecipientTestMode: payload?.emailRecipientTestMode,
        })
        .then((context): ConferenceRegistrationCampaignEmail => {
          const mailOptions = AttendeesEmailsService.getMailOptions(context);

          return Object.assign({}, new BaseCampaignsEmail(registrantDbId), {
            [BaseCampaignsEmailKeys.mailOptions]: mailOptions,
            [BaseCampaignsEmailKeys.context]: context,
            [BaseCampaignsEmailKeys.template]: templateName,
          });
        })
        .then(data =>
          this.conferenceRegistrationEmailCampaignService.sendEmailToRegistrant(conferenceDbId, registrantDbId, data),
        );
      promises.push(promise);
    });

    await Promise.all(promises);
  };

  private sendBeforeYouGoMassEmail = async (
    emailTemplate: EmailTemplateConfiguration,
    payload: SendRegistrantsEmailPayload,
  ): Promise<void> => {
    const promises: Promise<EmailToSendConfig>[] = [];
    const templateName = emailTemplate?.[EmailTemplateConfigurationKeys.templateName] as EmailTemplates;

    payload?.registrants.forEach(registrant => {
      const conferenceDbId = registrant?.[RegistrantModelKeys.conferenceDbId];
      const registrantDbId = registrant?.[BaseModelKeys.dbId];

      const promise: Promise<any> = this.registrationSummaryEmailService
        .generateRegistrationBeforeYouGoEmail(emailTemplate, {
          conferenceDbId,
          registrantDbId,
          isTestEmail: payload?.isTestEmail,
          emailRecipientTestMode: payload?.emailRecipientTestMode,
        })
        .then((context): ConferenceRegistrationCampaignEmail => {
          const mailOptions = AttendeesEmailsService.getMailOptions(context);

          return Object.assign({}, new BaseCampaignsEmail(registrantDbId), {
            [BaseCampaignsEmailKeys.mailOptions]: mailOptions,
            [BaseCampaignsEmailKeys.context]: context,
            [BaseCampaignsEmailKeys.template]: templateName,
          });
        })
        .then(data =>
          this.conferenceRegistrationEmailCampaignService.sendEmailToRegistrant(
            conferenceDbId,
            registrantDbId,
            data as any,
          ),
        );

      promises.push(promise);
    });

    await Promise.all(promises);
  };

  static getMailOptions(context): Options {
    const mailOptions: Options = {
      to: context?.[NotifyEmailKeys.email],
      subject: context?.[NotifyEmailKeys.subjectTitle],
    };

    if (!!context?.[NotifyEmailKeys.replyTo]) {
      Object.assign(mailOptions, { replyTo: context?.[NotifyEmailKeys.replyTo] });
    }
    if (context?.[NotifyEmailKeys.ccEmail]?.length) {
      Object.assign(mailOptions, { cc: context?.[NotifyEmailKeys.ccEmail] });
    }
    if (context?.[NotifyEmailKeys.bccEmail]?.length) {
      Object.assign(mailOptions, { bcc: context?.[NotifyEmailKeys.bccEmail] });
    }
    return mailOptions;
  }

  private isDynamicTemplateName(value: unknown): value is string {
    return typeof value === 'string' && value.startsWith(this.dynamicTemplatePrefix);
  }

  private getDynamicTemplateId(value: string | null | undefined): string | null {
    if (!value || !this.isDynamicTemplateName(value)) {
      return null;
    }

    const id = value.slice(this.dynamicTemplatePrefix.length).trim();
    return id || null;
  }

  private getRegistrantRecipients(registrant: Registrant): string[] {
    const registrantData = registrant?.[RegistrantModelKeys.data];
    const primaryEmail = registrantData?.[RegistrantKeys.primaryEmailAddress]?.[EmailAddressKeys.address];
    const inviteeEmail = registrantData?.[RegistrantKeys.inviteeEmail];

    return [primaryEmail, inviteeEmail].filter((email): email is string => Boolean(email));
  }
}
